// Risk appetite on the risk register: the thresholds a risk's score is compared with, the verdict
// per risk, what an acceptance above appetite needs, and the annual loss limit Exposure modelling
// is compared with. Rules on the server in backend/src/lib/riskAppetite.ts; contract in
// docs/RISK-APPETITE.md. Display only: GET /risks/appetite carries every verdict, the page never
// works one out. The overall threshold and the acceptance periods are operating parameters
// (Settings -> Operating parameters); per-category thresholds and the loss limit are set here and
// need org:admin (the server decides; a 403 leaves the panel read-only).

import { useEffect, useState } from "react";
import { Gauge, X } from "lucide-react";
import { api, ApiError, apiErrorMessage } from "../api/client";
import { PortalLink } from "./PortalLink";
import { theme, btnPrimary, btnSecondary, inputStyle, floatingSheet, type as text } from "../theme";

export const APPETITE_CATEGORIES = ["Confidentiality", "Availability", "Integrity", "Operational", "Financial", "Legal", "Reputational"] as const;
export type AppetiteCategory = (typeof APPETITE_CATEGORIES)[number];

export type LossPercentile = "p90" | "p95" | "p99";

export const LOSS_PERCENTILE_LABELS: Record<LossPercentile, string> = {
  p90: "a 1-in-10 year loss (90th percentile)",
  p95: "a 1-in-20 year loss (95th percentile)",
  p99: "a 1-in-100 year loss (99th percentile)",
};

export type AcceptanceIssue = "critical_accepted" | "no_approver" | "approved_by_owner" | "no_expiry" | "expiry_too_long" | "expired";

export const ACCEPTANCE_ISSUE_TEXT: Record<AcceptanceIssue, string> = {
  critical_accepted: "a risk in the critical band cannot be accepted; it must be treated",
  no_approver: "no approver other than the owner is recorded",
  approved_by_owner: "the owner approved their own acceptance",
  no_expiry: "the acceptance has no expiry date",
  expiry_too_long: "the acceptance runs longer than the band allows",
  expired: "the acceptance has expired and needs re-approval",
};

export type AcceptanceBand = "low" | "medium" | "high" | "critical";

// The acceptance bands of the risk method (lib/riskAppetite.ts acceptanceBand) - who may accept
// and for how long, not the register's display severity.
const BAND_LABEL: Record<AcceptanceBand, string> = {
  low: "Low (1-4)",
  medium: "Medium (5-9)",
  high: "High (10-16)",
  critical: "Critical (20-25)",
};

export interface AppetiteAssessment {
  riskId: string;
  score: number | null;
  appetite: number;
  verdict: "within" | "above" | "unscored";
  band: AcceptanceBand | null;
  /** Above appetite, nothing in progress brings it down, and it is not accepted. */
  untreated: boolean;
  accepted: boolean;
  acceptanceIssues: AcceptanceIssue[];
  maxAcceptanceMonths: number | null;
}

export interface RiskAppetiteView {
  settings: { byCategory: Partial<Record<AppetiteCategory, number>>; lossLimitGbp: number | null; lossPercentile: LossPercentile };
  params: { appetiteScore: number; mediumAcceptanceMonths: number; highAcceptanceMonths: number };
  /** The free-text appetite statement recorded before thresholds existed. */
  statement: string | null;
  categories: AppetiteCategory[];
  loss: { limitGbp: number; percentile: LossPercentile; portfolioGbp: number; above: boolean } | null;
  assessments: Record<string, AppetiteAssessment>;
}

export function loadRiskAppetite(): Promise<RiskAppetiteView> {
  return api.get<RiskAppetiteView>("/risks/appetite");
}

export const fmtGbp = (n: number) => `£${Math.round(n).toLocaleString("en-GB")}`;
const months = (n: number) => `${n} month${n === 1 ? "" : "s"}`;
const sentence = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** The latest expiry (yyyy-mm-dd) the band allows for an acceptance made on `acceptedInput`. */
export function latestAcceptanceExpiry(acceptedInput: string, monthsAllowed: number): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(acceptedInput)) return null;
  const d = new Date(`${acceptedInput}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + monthsAllowed);
  return d.toISOString().slice(0, 10);
}

// ── Register row chip ──────────────────────────────────────────────────────

/** Shown on a register row only when the risk is above appetite (and not closed). */
export function AppetiteChip({ assessment, status }: { assessment: AppetiteAssessment | undefined; status: string }) {
  if (!assessment || assessment.verdict !== "above" || status === "closed") return null;
  const issues = assessment.acceptanceIssues;
  const variant = assessment.accepted ? (issues.length ? "attention" : "accepted") : "above";
  const tone = variant === "attention"
    ? { color: theme.color.dangerText, bg: theme.color.dangerBg, label: "Acceptance needs attention" }
    : variant === "accepted"
      ? { color: theme.color.muted, bg: theme.color.panelAlt, label: "Accepted above appetite" }
      : { color: theme.color.warningText, bg: theme.color.warningBg, label: "Above appetite" };
  const title = [
    `Score ${assessment.score} is above the appetite of ${assessment.appetite}.`,
    ...issues.map((i) => `${sentence(ACCEPTANCE_ISSUE_TEXT[i])}.`),
    variant === "above" && assessment.untreated ? "Nothing in progress brings it below appetite." : null,
  ].filter(Boolean).join("\n");
  return (
    <span title={title} style={{ fontSize: 10, padding: "1px 6px", borderRadius: 2, background: tone.bg, color: tone.color, border: variant === "accepted" ? `1px solid ${theme.color.border}` : undefined, fontWeight: 600, whiteSpace: "nowrap" }}>
      {tone.label}
    </span>
  );
}

// ── Risk details: the verdict under Scoring ────────────────────────────────

export function AppetiteVerdict({ assessment, view, category, hasResidual }: {
  assessment: AppetiteAssessment | undefined;
  view: RiskAppetiteView;
  category: string;
  /** The verdict uses the residual score, else the inherent one; the line says which. */
  hasResidual: boolean;
}) {
  if (!assessment) return null;
  const own = view.settings.byCategory[category as AppetiteCategory] != null;
  const against = own ? `your appetite of ${assessment.appetite} for ${category}` : `your overall appetite of ${assessment.appetite}`;
  if (assessment.verdict === "unscored" || assessment.score == null) {
    return <span style={text.hint}>Score this risk to compare it with {against}.</span>;
  }
  const above = assessment.verdict === "above";
  const scoreWord = hasResidual ? `Residual ${assessment.score}` : `Inherent ${assessment.score} (no residual score yet)`;
  const band = assessment.band;
  return (
    <div style={{ display: "grid", gap: 4 }}>
      <span style={{ fontWeight: 600, color: above ? theme.color.dangerText : theme.color.successText }}>
        {scoreWord} is {above ? "above" : "within"} {against}
      </span>
      {band && (
        <span style={text.hint}>
          Acceptance band: {BAND_LABEL[band]}.{" "}
          {band === "critical" || assessment.maxAcceptanceMonths == null
            ? "A risk in this band cannot be accepted; it must be treated."
            : `Above appetite, an acceptance may run for up to ${months(assessment.maxAcceptanceMonths)} before it is re-approved.`}
        </span>
      )}
      {above && assessment.accepted && assessment.acceptanceIssues.length === 0 && (
        <span style={{ ...text.hint, color: theme.color.successText }}>Accepted above appetite; the acceptance meets your rules.</span>
      )}
      {above && assessment.accepted && assessment.acceptanceIssues.length > 0 && (
        <div>
          <span style={{ ...text.hint, color: theme.color.dangerText, fontWeight: 600 }}>Acceptance needs attention:</span>
          <ul style={{ margin: "2px 0 0", paddingLeft: 18, ...text.hint, color: theme.color.dangerText }}>
            {assessment.acceptanceIssues.map((i) => <li key={i}>{sentence(ACCEPTANCE_ISSUE_TEXT[i])}</li>)}
          </ul>
        </div>
      )}
      {above && !assessment.accepted && (
        <span style={{ ...text.hint, color: assessment.untreated ? theme.color.warningText : undefined }}>
          {assessment.untreated
            ? "Nothing in progress brings it below appetite: treat it, or accept it with an approver other than the owner and an expiry."
            : "A treatment in progress is meant to bring it below appetite."}
        </span>
      )}
    </div>
  );
}

// ── Treatment form: what an acceptance needs ───────────────────────────────

export function AcceptanceHint({ assessment }: { assessment: AppetiteAssessment | null | undefined }) {
  if (!assessment || assessment.verdict === "unscored" || assessment.score == null) {
    return <div style={{ ...text.hint, marginTop: 4 }}>Above your risk appetite, an acceptance needs an approver other than the owner and an expiry within the limit its band allows.</div>;
  }
  if (assessment.verdict === "within") {
    return <div style={{ ...text.hint, marginTop: 4 }}>Score {assessment.score} is within your appetite of {assessment.appetite}, so the owner may accept it. An approver and an expiry are still good practice.</div>;
  }
  if (assessment.band === "critical" || assessment.maxAcceptanceMonths == null) {
    return <div style={{ ...text.hint, marginTop: 4, color: theme.color.dangerText }}>Score {assessment.score} is in the critical band: it cannot be accepted and must be treated. An acceptance recorded here is flagged.</div>;
  }
  return (
    <div style={{ ...text.hint, marginTop: 4, color: theme.color.warningText }}>
      Score {assessment.score} is above your appetite of {assessment.appetite}: the acceptance needs an approver other than the owner and an
      expiry no more than {months(assessment.maxAcceptanceMonths)} after it was accepted.
    </div>
  );
}

// ── The appetite panel (opened from the register header) ──────────────────

const sectionTitle: React.CSSProperties = { ...text.eyebrow, margin: "18px 0 8px" };

export function RiskAppetiteDrawer({ view, canEdit, onClose, onSaved }: {
  view: RiskAppetiteView;
  /** org:admin; the server refuses anyone else, and a 403 turns the panel read-only. */
  canEdit: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Escape closes the panel, as it does the risk drawer beneath it (DOC-5).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const categories = view.categories.length ? view.categories : [...APPETITE_CATEGORIES];
  const overall = view.params.appetiteScore;
  const [byCategory, setByCategory] = useState<Record<string, string>>(
    () => Object.fromEntries(categories.map((c) => [c, view.settings.byCategory[c]?.toString() ?? ""])),
  );
  const [lossLimit, setLossLimit] = useState(view.settings.lossLimitGbp?.toString() ?? "");
  const [percentile, setPercentile] = useState<LossPercentile>(view.settings.lossPercentile);
  const [readOnly, setReadOnly] = useState(!canEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const validScore = (v: string) => v.trim() === "" || (Number.isInteger(Number(v)) && Number(v) >= 1 && Number(v) <= 25);
  const validLoss = lossLimit.trim() === "" || (Number.isInteger(Number(lossLimit)) && Number(lossLimit) >= 1);
  const valid = categories.every((c) => validScore(byCategory[c] ?? "")) && validLoss;

  async function save() {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await api.put("/risks/appetite", {
        // A blank box clears the category's own threshold, so it follows the overall one again.
        byCategory: Object.fromEntries(categories.map((c) => [c, (byCategory[c] ?? "").trim() === "" ? null : Number(byCategory[c])])),
        lossLimitGbp: lossLimit.trim() === "" ? null : Number(lossLimit),
        lossPercentile: percentile,
      });
      setNotice("Risk appetite saved. Every risk has been compared with it again.");
      onSaved();
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        setReadOnly(true);
        setError("Only an organisation administrator can change the risk appetite.");
      } else {
        setError(apiErrorMessage(e, "Could not save the risk appetite"));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "flex-end" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div role="dialog" aria-label="Risk appetite" style={{ ...floatingSheet, width: 480, maxWidth: "100vw", height: "100vh", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--c-line)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexShrink: 0 }}>
          <h2 style={{ ...text.sectionTitle, margin: 0, display: "flex", alignItems: "center", gap: 8 }}><Gauge size={16} /> Risk appetite</h2>
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.muted, padding: 0, display: "inline-flex" }}><X size={18} /></button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px 24px" }}>
          <p style={{ ...text.hint, margin: 0 }}>
            How much risk your organisation carries without treating it. A risk scored above its threshold is flagged on the register
            until it is treated below it, or accepted by someone other than its owner for no longer than its band allows. Nothing is blocked.
          </p>

          {view.statement && (
            <>
              <div style={sectionTitle}>Your recorded appetite statement</div>
              <blockquote style={{ margin: 0, padding: "4px 10px", borderLeft: `3px solid ${theme.color.ref}`, ...text.body, whiteSpace: "pre-wrap" }}>{view.statement}</blockquote>
              <div style={{ ...text.meta, marginTop: 4 }}>For context: the thresholds below are what the register compares each risk with.</div>
            </>
          )}

          <div style={sectionTitle}>Overall threshold</div>
          <div style={text.body}>
            Risks scored above <strong>score {overall}</strong> are flagged, unless their category has a threshold of its own.
          </div>
          <ul style={{ margin: "6px 0 0", paddingLeft: 18, ...text.hint }}>
            <li>{BAND_LABEL.medium}: an acceptance above appetite may run for up to {months(view.params.mediumAcceptanceMonths)}</li>
            <li>{BAND_LABEL.high}: up to {months(view.params.highAcceptanceMonths)}</li>
            <li>{BAND_LABEL.critical}: never accepted; it must be treated</li>
          </ul>
          <div style={{ ...text.hint, marginTop: 6 }}>
            The overall threshold and the acceptance periods are operating parameters:{" "}
            <PortalLink to="/operating-parameters" style={{ color: theme.color.ref }}>change them on Operating parameters</PortalLink>.
          </div>

          <div style={sectionTitle}>By category</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 110px", gap: "6px 12px", alignItems: "center" }}>
            {categories.map((c) => {
              const v = byCategory[c] ?? "";
              return (
                <div key={c} style={{ display: "contents" }}>
                  <label htmlFor={`appetite-${c}`} style={text.body}>{c}</label>
                  {readOnly ? (
                    <span style={{ ...text.body, color: v ? theme.color.text : theme.color.subtle }}>{v ? `score ${v}` : `overall (${overall})`}</span>
                  ) : (
                    <input
                      id={`appetite-${c}`} type="number" min={1} max={25} step={1} value={v} placeholder={`${overall}`}
                      onChange={(e) => setByCategory((prev) => ({ ...prev, [c]: e.target.value }))}
                      style={{ ...inputStyle, padding: "6px 10px", borderColor: validScore(v) ? undefined : theme.color.danger }}
                    />
                  )}
                </div>
              );
            })}
          </div>
          {!readOnly && <div style={{ ...text.meta, marginTop: 6 }}>A score from 1 to 25. Leave a box blank to use the overall threshold.</div>}

          <div style={sectionTitle}>Annual loss limit</div>
          {readOnly ? (
            <div style={text.body}>
              {view.settings.lossLimitGbp != null
                ? <>{fmtGbp(view.settings.lossLimitGbp)} at {LOSS_PERCENTILE_LABELS[view.settings.lossPercentile]}</>
                : <span style={{ color: theme.color.subtle }}>No loss limit set</span>}
            </div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={text.body}>£</span>
                <input
                  type="number" min={1} step={1000} value={lossLimit} placeholder="No limit set" aria-label="Annual loss limit in pounds"
                  onChange={(e) => setLossLimit(e.target.value)}
                  style={{ ...inputStyle, padding: "6px 10px", borderColor: validLoss ? undefined : theme.color.danger }}
                />
              </label>
              <select value={percentile} onChange={(e) => setPercentile(e.target.value as LossPercentile)} aria-label="Measured at" style={inputStyle}>
                {(Object.keys(LOSS_PERCENTILE_LABELS) as LossPercentile[]).map((p) => <option key={p} value={p}>{sentence(LOSS_PERCENTILE_LABELS[p])}</option>)}
              </select>
            </div>
          )}
          <div style={{ ...text.hint, marginTop: 6 }}>
            The most you will tolerate losing in a year, compared with the modelled loss on{" "}
            <PortalLink to="/exposure" style={{ color: theme.color.ref }}>Exposure modelling</PortalLink>.
          </div>
          {view.loss && (
            <div style={{ ...text.hint, marginTop: 4, fontWeight: 600, color: view.loss.above ? theme.color.dangerText : theme.color.successText }}>
              Modelled {fmtGbp(view.loss.portfolioGbp)} at {LOSS_PERCENTILE_LABELS[view.loss.percentile]}: {view.loss.above ? "above" : "within"} your limit.
            </div>
          )}

          {error && <div style={{ ...text.hint, color: theme.color.dangerText, marginTop: 14 }}>{error}</div>}
          {notice && <div style={{ ...text.hint, color: theme.color.successText, marginTop: 14 }}>{notice}</div>}
          {readOnly && !error && <div style={{ ...text.meta, marginTop: 14 }}>Only an organisation administrator can change the risk appetite.</div>}
        </div>

        {!readOnly && (
          <div style={{ padding: "12px 20px", borderTop: "1px solid var(--c-line)", display: "flex", gap: 8, flexShrink: 0 }}>
            <button type="button" style={btnPrimary} disabled={!valid || saving} onClick={() => void save()}>{saving ? "Saving…" : "Save"}</button>
            <button type="button" style={btnSecondary} onClick={onClose}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}
